import React from 'react';

import {IconSize, iconStrokeWidthBySize, IconStyle} from '../../icon_types';

type HelpIconProps = {
  id?: string;
  className?: string;
  size: IconSize;
  color?: string;
  style: IconStyle;
};

const notFilledPathsBySize = {
  '16': [
    'M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667Z',
    'M6.48 6.40002C6.6224 6.02223 6.8853 5.70005 7.22812 5.47819C7.57094 5.25633 7.97084 5.14861 8.37765 5.17068C8.78447 5.19275 9.17152 5.3432 9.47998 5.60002C9.78843 5.85684 10 6.20961 10 6.58002C10 8.00002 7.99999 8.66669 7.99999 8.66669',
    'M8 10.6667H8.00667',
  ],
  '24': [
    'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
    'M9.09009 9C9.32519 8.33167 9.78924 7.76811 10.4 7.40913C11.0108 7.05016 11.729 6.91894 12.4273 7.03871C13.1255 7.15849 13.7589 7.52153 14.2152 8.06353C14.6714 8.60553 14.9211 9.29152 14.9201 10C14.9201 12 11.9201 13 11.9201 13',
    'M12 17H12.01',
  ],
  '32': [
    'M16.0001 29.3333C23.3639 29.3333 29.3334 23.3638 29.3334 16C29.3334 8.6362 23.3639 2.66666 16.0001 2.66666C8.63628 2.66666 2.66675 8.6362 2.66675 16C2.66675 23.3638 8.63628 29.3333 16.0001 29.3333Z',
    'M12.1201 12C12.4336 11.1089 13.0523 10.3575 13.8667 9.87885C14.6811 9.40022 15.6386 9.22526 16.5697 9.38495C17.5007 9.54465 18.3452 10.0287 18.9536 10.7514C19.5619 11.474 19.8949 12.3887 19.8935 13.3333C19.8935 16 15.8935 17.3333 15.8935 17.3333"',
    'M16 22.6667H16.0133',
  ],
};

const filledPathsBySize = {
  '16': [
    'M7.99992 14.6667C11.6818 14.6667 14.6666 11.6819 14.6666 8C14.6666 4.3181 11.6818 1.33333 7.99992 1.33333C4.31802 1.33333 1.33325 4.3181 1.33325 8C1.33325 11.6819 4.31802 14.6667 7.99992 14.6667Z',
    'M6.06006 6C6.21679 5.55445 6.52616 5.17874 6.93336 4.93943C7.34056 4.70011 7.81932 4.61263 8.28484 4.69248C8.75036 4.77233 9.1726 5.01435 9.47678 5.37569C9.78095 5.73702 9.94743 6.19435 9.94673 6.66667C9.94673 8 7.94673 8.66667 7.94673 8.66667"',
    'M8 11.3333H8.00722',
  ],
  '24': [
    'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
    'M9.09009 9.00001C9.32519 8.33167 9.78924 7.76811 10.4 7.40914C11.0108 7.05016 11.729 6.91894 12.4273 7.03872C13.1255 7.15849 13.7589 7.52153 14.2152 8.06353C14.6714 8.60554 14.9211 9.29153 14.9201 10C14.9201 12 11.9201 13 11.9201 13',
    'M12 17H12.0108',
  ],
  '32': [
    'M16.0001 29.3333C23.3639 29.3333 29.3334 23.3638 29.3334 16C29.3334 8.6362 23.3639 2.66666 16.0001 2.66666C8.63628 2.66666 2.66675 8.6362 2.66675 16C2.66675 23.3638 8.63628 29.3333 16.0001 29.3333Z',
    'M12.1201 12C12.4336 11.1089 13.0523 10.3575 13.8667 9.87885C14.6811 9.40022 15.6386 9.22526 16.5697 9.38495C17.5007 9.54465 18.3452 10.0287 18.9536 10.7514C19.5619 11.474 19.8949 12.3887 19.8935 13.3333C19.8935 16 15.8935 17.3333 15.8935 17.3333',
    'M16 22.6667H16.0133',
  ],
};

const HelpIcon = (props: HelpIconProps) => {
  const {id, className, size, color, style} = props;
  const paths =
    style === 'filled' ? filledPathsBySize[size] : notFilledPathsBySize[size];
  const colorOptions = color || 'currentColor' || '#1E1E20';
  const strokeWidth = iconStrokeWidthBySize[size];

  return (
    <svg
      id={id}
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none">
      <path
        d={paths[0]}
        stroke={style !== 'filled' ? colorOptions : 'white'}
        fill={style === 'filled' ? colorOptions : 'transparent'}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={paths[1]}
        stroke={style !== 'filled' ? colorOptions : 'white'}
        fill={style === 'filled' ? undefined : 'none'}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={paths[2]}
        stroke={style !== 'filled' ? colorOptions : 'white'}
        fill={style === 'filled' ? 'white' : 'none'}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default HelpIcon;
